import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { reloadAPI, userAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';
import { Search, RefreshCw, Link2, MapPin, Package, Save } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

// Map bounds fitter component
const MapBoundsFitter = ({ markers }) => {
  const map = useMap();
  
  useEffect(() => {
    if (markers.length > 0) {
      const validMarkers = markers.filter(m => m.lat && m.lng);
      if (validMarkers.length > 0) {
        const bounds = validMarkers.map(m => [m.lat, m.lng]);
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    }
  }, [markers, map]);
  
  return null;
};

const ReloadDatabase = () => {
  const navigate = useNavigate();
  const { isCarrier, isCustomer } = useAuth();
  const [loads, setLoads] = useState([]);
  const [trucks, setTrucks] = useState([]); // For tracking trucks (loads in transit)
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    zipCode: '',
    pickupDate: '',
    deliveryDate: ''
  });
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  
  const isCarrierUser = isCarrier();
  const isCustomerUser = isCustomer();

  // Load user preferences on mount
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const res = await userAPI.getPreferences();
        const prefs = res.data?.preferences || {};
        if (prefs.reload_filters) {
          setFilters(prev => ({ ...prev, ...prefs.reload_filters }));
        }
        setPreferencesLoaded(true);
      } catch (error) {
        console.error('Failed to load preferences:', error);
        setPreferencesLoaded(true);
      }
    };
    loadPreferences();
  }, []);

  const fetchLoads = useCallback(async (activeFilters) => {
    setLoading(true);
    try {
      const res = await reloadAPI.getMapData(activeFilters);
      
      // Separate upcoming loads (purple) and trucks in transit (orange)
      // Upcoming = Booked (new model) + legacy Available/Covered/Rate Con Sent
      const upcomingStatuses = ['Booked', 'Available', 'Covered', 'Rate Con Sent'];
      const truckStatuses = ['In Transit', 'In Transit MX', 'In Transit US', 'At the Shipper', 'At Pickup'];
      
      const upcomingLoads = res.data.filter(l => upcomingStatuses.includes(l.status));
      const inTransitLoads = res.data.filter(l => truckStatuses.includes(l.status));
      
      setLoads(upcomingLoads);
      setTrucks(inTransitLoads);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch loads after preferences are loaded
  useEffect(() => {
    if (preferencesLoaded) {
      fetchLoads(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferencesLoaded, fetchLoads]);

  // Save filters to user preferences
  const saveFilters = async () => {
    try {
      await userAPI.updatePreferenceKey('reload_filters', filters);
      toast.success('Filters saved to your profile');
    } catch (error) {
      toast.error('Failed to save filters');
    }
  };

  const handleSearch = () => {
    fetchLoads(filters);
  };

  const handleClearFilters = () => {
    const cleared = { zipCode: '', pickupDate: '', deliveryDate: '' };
    setFilters(cleared);
    fetchLoads(cleared);
  };

  // Filter loads based on search criteria
  const filteredLoads = useMemo(() => {
    let result = loads;
    
    // Filter by zip code
    if (filters.zipCode) {
      result = result.filter(load => {
        const pickups = load.pickups || [];
        return pickups.some(p => p.zip_code?.startsWith(filters.zipCode));
      });
    }
    
    // Filter by pickup date
    if (filters.pickupDate) {
      result = result.filter(load => {
        const pickups = load.pickups || [];
        return pickups.some(p => {
          if (!p.appointment_datetime) return false;
          const pickupDateStr = p.appointment_datetime.split('T')[0];
          return pickupDateStr === filters.pickupDate;
        });
      });
    }
    
    // Filter by delivery date
    if (filters.deliveryDate) {
      result = result.filter(load => {
        const deliveries = load.deliveries || [];
        return deliveries.some(d => {
          if (!d.appointment_datetime) return false;
          const deliveryDateStr = d.appointment_datetime.split('T')[0];
          return deliveryDateStr === filters.deliveryDate;
        });
      });
    }
    
    return result;
  }, [loads, filters]);

  // Collect all markers for the map
  const allMarkers = useMemo(() => {
    const markers = [];
    
    // Purple dots: Available loads at pickup locations
    filteredLoads.forEach(load => {
      (load.pickups || []).forEach(pickup => {
        if (pickup.lat && pickup.lng) {
          markers.push({
            ...pickup,
            type: 'available',
            color: '#9333ea', // purple for available loads
            load,
            markerType: 'load'
          });
        }
      });
    });
    
    // Orange dots: Trucks at final delivery locations (for non-carriers and non-customers)
    // For carriers: Show their own trucks
    if (!isCustomerUser) {
      trucks.forEach(load => {
        const deliveries = load.deliveries || [];
        const finalDelivery = deliveries[deliveries.length - 1]; // Last delivery = expected empty location
        
        if (finalDelivery?.lat && finalDelivery?.lng) {
          markers.push({
            ...finalDelivery,
            type: 'truck',
            color: '#f97316', // orange for trucks
            load,
            markerType: 'truck'
          });
        }
      });
    }
    
    return markers;
  }, [filteredLoads, trucks, isCustomerUser]);

  // Find nearby loads for matching
  const findNearbyLoads = (selectedMarker) => {
    if (!selectedMarker) return [];
    
    const nearbyLoads = [];
    const RADIUS = 100; // miles radius to consider "nearby"
    
    // Calculate distance between two coordinates (Haversine formula)
    const getDistance = (lat1, lng1, lat2, lng2) => {
      const R = 3959; // Earth's radius in miles
      const dLat = (lat2 - lat1) * Math.PI / 180;
      const dLng = (lng2 - lng1) * Math.PI / 180;
      const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return R * c;
    };
    
    // If a truck is selected, find nearby available loads
    if (selectedMarker.markerType === 'truck') {
      filteredLoads.forEach(load => {
        (load.pickups || []).forEach(pickup => {
          if (pickup.lat && pickup.lng) {
            const distance = getDistance(selectedMarker.lat, selectedMarker.lng, pickup.lat, pickup.lng);
            if (distance <= RADIUS) {
              nearbyLoads.push({
                ...load,
                matchType: 'pickup',
                matchCity: `${pickup.city}, ${pickup.state}`,
                distance: Math.round(distance)
              });
            }
          }
        });
      });
    }
    
    // Sort by distance
    nearbyLoads.sort((a, b) => a.distance - b.distance);
    return nearbyLoads;
  };

  const nearbyLoads = useMemo(() => {
    if (!selectedLoad) return [];
    const marker = allMarkers.find(m => m.load?.load_id === selectedLoad.load_id);
    return findNearbyLoads(marker);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLoad, allMarkers, filteredLoads]);

  return (
    <MainLayout>
      <div className=" h-[calc(100vh-110px)]" data-testid="reload-database-page">
        {/* Header */}
        <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl md:text-3xl font-bold text-[var(--tms-text)]">
              {isCarrierUser ? 'Available Loads' : 'ReLoad'}
            </h1>
            <p className="text-xs md:text-base text-[var(--tms-text-secondary)] mt-1">
              {isCarrierUser ? 'Browse available loads for pickup' : 'Match pickups & deliveries'}
            </p>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchLoads}
            data-testid="refresh-button"
          >
            <RefreshCw className="w-4 h-4 sm:mr-1" /> 
            <span className="hidden sm:inline">Refresh</span>
          </Button>
        </div>

        {/* Search Bar - Visible for all users */}
        <div className="mb-4 p-3 md:p-4 bg-[var(--tms-card)] border rounded-lg shadow-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3 md:gap-4 items-end">
            <div className="space-y-1">
              <Label className="text-xs md:text-sm">Zip Code</Label>
              <Input
                placeholder="e.g. 77001"
                value={filters.zipCode}
                onChange={(e) => setFilters({ ...filters, zipCode: e.target.value })}
                data-testid="filter-zip-code"
                className="text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs md:text-sm">Pickup Date</Label>
              <Input
                type="date"
                value={filters.pickupDate}
                onChange={(e) => setFilters({ ...filters, pickupDate: e.target.value })}
                data-testid="filter-pickup-date"
                className="text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs md:text-sm">Delivery Date</Label>
              <Input
                type="date"
                value={filters.deliveryDate}
                onChange={(e) => setFilters({ ...filters, deliveryDate: e.target.value })}
                data-testid="filter-delivery-date"
                className="text-sm"
              />
            </div>
            <div className="flex items-end gap-2 col-span-1 sm:col-span-2 md:col-span-2">
              <Button size="sm" onClick={handleSearch} data-testid="search-button" className="flex-1 sm:flex-none">
                <Search className="w-4 h-4 mr-1" />
                Search
              </Button>
              <Button size="sm" variant="outline" onClick={handleClearFilters}>
                Clear
              </Button>
              <Button size="sm" variant="outline" onClick={saveFilters} title="Save filters to your profile" data-testid="save-filters">
                <Save className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content - Split View */}
        <div className="flex flex-col md:flex-row gap-4 h-auto md:h-[calc(100%-180px)]">
          {/* Left Panel - Loads List */}
          <div className="w-full md:w-1/3 h-64 md:h-full bg-[var(--tms-card)] border rounded-lg overflow-hidden flex flex-col order-2 md:order-1">
            <div className="p-2 md:p-3 bg-[var(--tms-hover)] border-b">
              <h3 className="font-semibold text-[var(--tms-text)] flex items-center gap-2 text-sm md:text-base">
                <Package className="w-4 h-4" />
                Available Loads ({filteredLoads.length})
              </h3>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center h-full text-[var(--tms-text-muted)]">
                  Loading...
                </div>
              ) : filteredLoads.length === 0 ? (
                <div className="flex items-center justify-center h-full text-[var(--tms-text-muted)]">
                  No loads found
                </div>
              ) : (
                <div className="divide-y divide-[var(--tms-border)]">
                  {filteredLoads.map((load) => (
                    <div
                      key={load.load_id}
                      className={`p-3 cursor-pointer hover:bg-[var(--tms-hover)] transition-colors ${
                        selectedLoad?.load_id === load.load_id ? 'bg-[var(--tms-primary)]/10 border-l-4 border-[var(--tms-primary)]' : ''
                      }`}
                      onClick={() => setSelectedLoad(load)}
                      data-testid={`load-item-${load.load_id}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-mono font-semibold text-sm text-[var(--tms-text)]">
                          #{load.load_number}
                        </span>
                      </div>
                      <p className="text-sm text-[var(--tms-text-secondary)] truncate">{load.customer_name}</p>
                      <p className="text-xs text-[var(--tms-text-muted)]">{load.equipment}</p>
                      
                      {/* Pickup/Delivery summary */}
                      <div className="mt-2 space-y-1">
                        {(load.pickups || []).slice(0, 1).map((p, i) => (
                          <div key={`p-${i}`} className="flex items-center gap-1 text-xs">
                            <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                            <span className="truncate text-[var(--tms-text)]">{p.city}, {p.state}</span>
                            {p.appointment_datetime && (
                              <span className="text-[var(--tms-text-muted)] ml-auto">
                                {new Date(p.appointment_datetime).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        ))}
                        {(load.deliveries || []).slice(0, 1).map((d, i) => (
                          <div key={`d-${i}`} className="flex items-center gap-1 text-xs">
                            <span className="w-2 h-2 rounded-full bg-green-500"></span>
                            <span className="truncate text-[var(--tms-text)]">{d.city}, {d.state}</span>
                            {d.appointment_datetime && (
                              <span className="text-[var(--tms-text-muted)] ml-auto">
                                {new Date(d.appointment_datetime).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Nearby Matches Panel - Show when a truck is selected */}
            {selectedLoad && nearbyLoads.length > 0 && (
              <div className="border-t border-[var(--tms-border)] bg-[var(--tms-warning)]/10 p-3 max-h-48 overflow-y-auto">
                <h4 className="font-semibold text-sm text-[var(--tms-warning)] flex items-center gap-1 mb-2">
                  <Link2 className="w-4 h-4" />
                  Nearby Loads ({nearbyLoads.length})
                </h4>
                <div className="space-y-2">
                  {nearbyLoads.slice(0, 5).map((match, idx) => (
                    <div 
                      key={`match-${idx}`}
                      className="p-2 bg-[var(--tms-card)] rounded border border-[var(--tms-border)] text-xs cursor-pointer hover:bg-[var(--tms-hover)]"
                      onClick={() => navigate(`/loads/${match.load_id}`)}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-mono font-medium text-[var(--tms-text)]">#{match.load_number}</span>
                        <span className="text-[var(--tms-text-muted)]">{match.distance} mi</span>
                      </div>
                      <p className="text-[var(--tms-text-secondary)] truncate">{match.customer_name}</p>
                      <div className="flex items-center gap-1 mt-1">
                        <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                        <span className="text-[var(--tms-text)]">{match.matchCity}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Panel - Map */}
          <div className="flex-1 min-h-[300px] md:min-h-0 bg-[var(--tms-card)] border rounded-lg overflow-hidden relative order-1 md:order-2">
            {/* Map Legend - smaller on mobile */}
            <div className="absolute top-2 right-2 z-[1000] bg-[var(--tms-card)] border rounded-lg p-1.5 md:p-2 shadow-md">
              <div className="text-[10px] md:text-xs space-y-1">
                <div className="flex items-center gap-1.5 md:gap-2">
                  <span className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-purple-500"></span>
                  <span className="text-[var(--tms-text)]">Available</span>
                </div>
                {!isCustomerUser && (
                  <div className="flex items-center gap-1.5 md:gap-2">
                    <span className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-orange-500"></span>
                    <span className="text-[var(--tms-text)]">Trucks</span>
                  </div>
                )}
              </div>
            </div>

            <MapContainer
              center={[39.8283, -98.5795]} // Center of US
              zoom={4}
              style={{ height: '100%', width: '100%' }}
              data-testid="reload-map"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              
              <MapBoundsFitter markers={allMarkers} />
              
              {allMarkers.map((marker, idx) => (
                <CircleMarker
                  key={`marker-${idx}`}
                  center={[marker.lat, marker.lng]}
                  radius={8}
                  fillColor={marker.color}
                  color={marker.load?.load_id === selectedLoad?.load_id ? '#2563eb' : '#ffffff'}
                  weight={marker.load?.load_id === selectedLoad?.load_id ? 3 : 2}
                  fillOpacity={0.8}
                  eventHandlers={{
                    click: () => setSelectedLoad(marker.load)
                  }}
                >
                  <Popup>
                    <div className="min-w-48">
                      <p className="font-semibold">
                        {marker.markerType === 'truck' ? 'Truck' : 'Load'} #{marker.load?.load_number}
                      </p>
                      <p className="text-sm text-gray-600">{marker.load?.customer_name}</p>
                      <div className="flex items-center gap-1 mt-1">
                        <span className={`w-2 h-2 rounded-full ${marker.markerType === 'truck' ? 'bg-orange-500' : 'bg-purple-500'}`}></span>
                        <span className="text-sm">
                          {marker.markerType === 'truck' ? 'Expected Empty' : 'Pickup Location'}
                        </span>
                      </div>
                      <p className="text-sm mt-1">
                        <MapPin className="w-3 h-3 inline mr-1" />
                        {marker.facility_name}
                      </p>
                      <p className="text-xs text-gray-500">{marker.city}, {marker.state} {marker.zip_code}</p>
                      {marker.appointment_datetime && (
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(marker.appointment_datetime).toLocaleString()}
                        </p>
                      )}
                      <Button
                        size="sm"
                        className="mt-2 w-full"
                        onClick={() => navigate(`/loads/${marker.load?.load_id}`)}
                      >
                        View Load
                      </Button>
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default ReloadDatabase;
